import React from 'react';
import ReactDOM from 'react-dom';
import * as elog from '@reverbdotcom/commons/src/elog';
import { AppProvider, buildApolloClient } from '@reverbdotcom/discovery-ui';
import { parseMeta, frontendCurrentUser } from '@reverbdotcom/discovery-ui/src/components/parse_meta';
import { get } from 'lodash';
import { offlineInit } from '@eppo/js-client-sdk';
import { assignmentLogger } from '@reverbdotcom/commons/src/eppo/eppoAssignmentLogger';
import { IUser } from '@reverbdotcom/commons';
import { clientRouterInitialized, getClientRouter } from '@reverbdotcom/discovery-ui/src/reverb-js-common/build_reverb_router';

const ROUTER_COMPONENT_NAME = 'Reverb.Router';
const CLASS_NAME_ATTR = 'data-react-class';
const PROPS_ATTR = 'data-react-props';

const user = get(frontendCurrentUser(), 'user', {}) as IUser;
const state = parseMeta('apollo-state');

const appConfig = parseMeta('app-config-meta');

const client = buildApolloClient(user);
client.restore(state);

const render = (global: typeof window, dom: typeof document) => {
  const eppoClient = offlineInit({
    flagsConfiguration: appConfig?.config?.eppoFlagsConfiguration?.flags || {},
    isObfuscated: false,
    assignmentLogger,
  });

  const all = dom.querySelectorAll('[data-react-class]');
  [].forEach.call(all, async (domElement: Element) => { // querySelectorAll returns a NodeList not an []
    try {
      const componentName = domElement.getAttribute(CLASS_NAME_ATTR);
      const propsStr = domElement.getAttribute(PROPS_ATTR);
      const props = JSON.parse(propsStr);

      const constructor = componentName
        .split('.')
        .reduce((obj, index) => obj[index], global);

      if (!constructor) {
        throw new Error(`Could not find React component ${componentName}`);
      }

      if (componentName === ROUTER_COMPONENT_NAME) {
        await clientRouterInitialized(getClientRouter());
      }

      const componentElement = React.createElement(constructor, props);

      const wrapped = React.createElement(
        AppProvider,
        {
          user,
          client,
          eppoClient,
          url: window.location.href,
          referer: document.referrer,
        },
        componentElement,
      );

      const hasSSRContent = domElement.hasChildNodes();

      if (hasSSRContent) {
        ReactDOM.hydrate(wrapped, domElement);
      } else {
        ReactDOM.render(wrapped, domElement);
      }
    } catch (e) {
      elog.error('react-mount-error', {}, e);
    }
  });
};

export default {
  render,
};

document.addEventListener('DOMContentLoaded', render.bind(this, window, document));
